import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { useApp } from '@/hooks/use-app-context';
import { themeImportErrorMessage } from '@/api/theme-import-history.contract';
import { useThemeImports } from './use-theme-imports';
import { themesTabHref } from './themes-tabs';
import { ThemeImportsHeader } from './ThemeImportsHeader';
import { ThemeImportsFilters } from './ThemeImportsFilters';
import { ThemeImportsTable } from './ThemeImportsTable';

export function ThemeImportsTab({ language }: { language: 'fr' | 'en' }) {
  const { t } = useApp();
  const navigate = useNavigate();
  const copy = t.admin.excelFiles.themes;
  const imports = useThemeImports();
  const isDeletingId =
    imports.deleteMutation.isPending && imports.deleteMutation.variables !== undefined
      ? imports.deleteMutation.variables
      : null;

  // Themes are added in the next tab of the same page.
  const openImportPage = () => {
    navigate(themesTabHref(language, 'new'));
  };

  return (
    <div className="flex w-full flex-col gap-8 pt-7">
      <ThemeImportsHeader
        title={copy.title}
        description={copy.description}
        totalCount={imports.counts.total}
        activeCount={imports.counts.active}
        inactiveCount={imports.counts.inactive}
        totalLabel={copy.totalImports}
        activeLabel={copy.activeImports}
        inactiveLabel={copy.inactiveImports}
        importLabel={copy.importAction}
        refreshLabel={copy.refresh}
        isLoading={imports.query.isFetching}
        onImport={openImportPage}
        onRefresh={imports.refresh}
      />

      <ThemeImportsFilters
        filterBySectorLabel={copy.filterBySector}
        allSectorsLabel={copy.allSectors}
        filterByStatusLabel={copy.filterByStatus}
        allStatusesLabel={copy.allStatuses}
        activeLabel={copy.active}
        inactiveLabel={copy.inactive}
        searchPlaceholder={copy.searchPlaceholder}
        sectors={imports.sectors}
        sector={imports.sector}
        status={imports.status}
        search={imports.search}
        onSectorChange={imports.setSector}
        onStatusChange={imports.setStatus}
        onSearchChange={imports.setSearch}
      />

      {imports.query.isError && !imports.query.data && (
        <div role="alert" className="flex flex-wrap items-center gap-3 rounded-xl border border-destructive/30 p-3 text-sm text-destructive">
          <span>{themeImportErrorMessage(imports.query.error, copy.errors)}</span>
          <Button variant="outline" onClick={() => void imports.query.refetch()}>
            {copy.retry}
          </Button>
        </div>
      )}
      {imports.query.isError && imports.query.data && (
        <p role="alert" className="text-sm text-destructive">
          {themeImportErrorMessage(imports.query.error, copy.errors)}
        </p>
      )}

      <ThemeImportsTable
        rows={imports.rows}
        isLoading={imports.query.isPending}
        hasNextPage={imports.query.hasNextPage}
        isFetchingNextPage={imports.query.isFetchingNextPage}
        loadedCount={copy.loadedCount.replace('{count}', String(imports.rows.length))}
        more={copy.more}
        loadingMore={copy.loadingMore}
        loading={copy.loading}
        noFiles={copy.noFiles}
        labels={{
          colFile: copy.colFile,
          colTheme: copy.colTheme,
          colSector: copy.colSector,
          colQuestions: copy.colQuestions,
          colAuthor: copy.colAuthor,
          colDate: copy.colDate,
          colStatus: copy.colStatus,
          colActions: copy.colActions,
          unknownAuthor: copy.unknownAuthor,
          active: copy.active,
          inactive: copy.inactive,
          previewQuestions: copy.previewQuestions,
          editQuestions: copy.editQuestions,
          updateQuestions: copy.updateQuestions,
          delete: copy.delete,
          cancel: copy.cancel,
          deleteTitle: copy.deleteTitle,
          deleteDescription: copy.deleteDescription,
          questionsDialog: copy.questions,
        }}
        isDeletingId={isDeletingId}
        onDelete={(id) => imports.deleteMutation.mutate(id)}
        onLoadMore={() => void imports.query.fetchNextPage()}
      />
    </div>
  );
}
